import { useMemo } from 'react';

import {
  ProgressBarItemMultiModified,
  ProgressBarItemSingleModified,
} from 'components/ProgressBar/context';

export interface ProgressBarItemMultiHook {
  showHeader: boolean;
  showFooter: boolean;
  title: string;
  trackClassName: string;
  stepLabel?: string;
  labelClassName: string;
  active: boolean;
  substeps: ProgressBarItemSingleModified[];
  last: boolean;
}

export default function useProgressBarItemMulti(
  props: Omit<ProgressBarItemMultiModified, 'type'>,
): ProgressBarItemMultiHook {
  const { title, index, active, passed, first, last, substeps } = props;

  const showHeader = useMemo(() => !first, [first]);
  const showFooter = useMemo(() => !last, [last]);

  const trackClassName = useMemo(
    () =>
      [
        'w-8 h-8 min-w-8 min-h-8 bg-neutral-300 flex',
        showHeader ? undefined : 'rounded-t-full',
      ]
        .filter(Boolean)
        .join(' '),
    [showHeader],
  );

  const bgColor = useMemo(() => {
    if (active) return 'bg-neutral-900';
    if (passed) return 'bg-purple-300';
    return 'bg-neutral-500';
  }, [active, passed]);

  const labelClassName = useMemo(
    () =>
      [
        'w-6 h-6 min-w-6 min-h-6 rounded-full flex text-neutral-000 text-14-22-em m-auto',
        bgColor,
      ]
        .filter(Boolean)
        .join(' '),
    [bgColor],
  );

  const stepLabel = useMemo(
    () => (passed ? undefined : `${index + 1}`),
    [passed, index],
  );

  return {
    showHeader,
    showFooter,
    title,
    trackClassName,
    stepLabel,
    labelClassName,
    active,
    substeps,
    last,
  };
}
